import {processValue} from './TabHelpers.jsx';
import {tryJson} from '@helpers/common/json.mjs';
import {useIsMobile} from '@helpers/hooks/useWindow';

export const Tabs2Specifications = ({value}) => {
  let json = value;

  if (typeof value === 'string') {
    json = JSON.parse(value);
  }

  if (!json || !('specs' in json)) {
    return null;
  }

  const sortOrder = ['Product Info', 'General Specifications', 'Technical Specifications'];

  const sortedSpecs = json?.specs?.sort((a, b) => {
    const indexA = sortOrder.indexOf(a.title);
    const indexB = sortOrder.indexOf(b.title);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    if (indexA !== -1) {
      return -1;
    }

    if (indexB !== -1) {
      return 1;
    }

    return a.title.localeCompare(b.title);
  });

  return (
    <div id="Tab2Specifications">
      {sortedSpecs?.map(({title, table}, index) => (
        <PropsCard key={index} title={title} table={table} />
      ))}
    </div>
  );
};

export const PropsCard = ({title, table}) => {
  const validProps = validKeys(table);
  if (!validProps?.length) {
    return null;
  }
  return (
    <div className="mt-4 mb-2">
      <Card>
        <div className="font-bold">{title}</div>
        <SingleTableSpecifications json={table} />
      </Card>
    </div>
  );
};

const sortInputBySeriesOrder = (validKeys) => {
  const seriesOrder = ['IX', 'VD', 'VS', 'SDR', 'SVR', 'DDX', 'EVL', 'VXF', 'ZVX'];
  const sortedKeys = seriesOrder?.filter((key) => validKeys.includes(key));
  const otherKeys = validKeys?.filter((key) => !seriesOrder.includes(key));
  return [...sortedKeys, ...otherKeys];
};

export const validKeys = (json) => {
  if (!json) {
    return [];
  }
  let validKeys = Object.keys(json);
  validKeys = validKeys?.filter((key) => {
    const value = json[key];

    const asJson = tryJson(value);
    if (asJson && Array.isArray(asJson)) {
      const validValues = asJson.filter(Boolean);
      if (!validValues.length) {
        return false;
      }
    }
    return value && value !== '--' && value !== 'N/A' && value !== null && value !== '?? R.Q. IVF';
  });
  if (!validKeys) {
    return [];
  }
  const sortedKeys = sortInputBySeriesOrder(validKeys);
  return sortedKeys;
};

export const SingleTableSpecifications = ({json, keySuffix, type}) => {
  // console.log({json, keySuffix, type});
  const CLASSES = 'border border-gray-500 md:border-slate-200 border-collapse specs-table-text';

  const requiresSizeKeySuffix = type === 'Empty';

  return (
    <table className={`my-1 w-full ${CLASSES}`}>
      <tbody className="not-prose">
        {validKeys(json)?.map((key) => {
          // Use a conditional to check if the key includes 'ZVX', and alter the keySuffix accordingly
          let modifiedKeySuffix = keySuffix;
          if (key?.includes('ZVX')) {
            if (key?.includes('-12 D2/D4') || modifiedKeySuffix?.includes('-12 D2/D4')) {
              modifiedKeySuffix = '-12 D1/D2';
            }
            if (key?.includes('-15 D2/D4') || modifiedKeySuffix?.includes('-15 D2/D4')) {
              modifiedKeySuffix = '-15 D1/D2';
            }
            if (key?.includes('-18 D2/D4') || modifiedKeySuffix?.includes('-18 D2/D4')) {
              modifiedKeySuffix = '-18 D1/D2';
            }
          }

          return (
            <tr key={key} className={`${CLASSES} lg:even:bg-slate-100`}>
              <th className={`${CLASSES} font-light text-left px-2`}>
                {requiresSizeKeySuffix ? `${key}${modifiedKeySuffix}` : key}
              </th>
              <td className={`${CLASSES} px-2`}>
                <div dangerouslySetInnerHTML={{__html: processValue(json[key])}} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export function SpecificationBulletPoints({value}) {
  if (!value) {
    return null;
  }
  const lines = String(value)
    ?.split('\n')
    ?.map((x) => x?.trim())
    ?.filter((x) => x);

  return (
    <ul>
      {lines?.map((x, index) => (
        <li key={index}>{x}</li>
      ))}
    </ul>
  );
}

export function Card({children}) {
  const isMobile = useIsMobile();
  return (
    <div
      className={
        'rounded overflow-hidden border border-gray-500 md:border-slate-200 px-4 py-2 bg-transparent'
      }>
      {children}
    </div>
  );
}
